import Component from '@glimmer/component'
import { ThemeKeys } from 'teacher-ui-client/utils/themes'

interface Signature {
  Element: HTMLTextAreaElement
  Args: {
    themeKey?: ThemeKeys
    value?: string
  }
}

/**
 * A themed textarea component
 *
 * Usage:
 * <Base::TextArea @value={{this.myValue}} {{on "input" (event-value this.setMyValue)}} />
 */
export class TextArea extends Component<Signature> {
  get themeClass() {
    const { themeKey = ThemeKeys.default } = this.args

    /* Typically inputs only want their themed-outline applied when focused. In some cases, like for a validation error,
     * the outline classes should be applied even when not focused.
     *
     * Additionally, we can only transition smoothly using the outline colour (and not on outline width or style)
     *
     * This is why the logic is based on the colour, rather than just always setting the colour and instead switching
     * the outline width from 0 to 2.
     */
    const themes: Partial<Record<ThemeKeys, string>> = {
      [ThemeKeys.blue]: 'focus:border-oceany-blue-400 focus:outline-oceany-blue-300/75',
      [ThemeKeys.green]: 'focus:border-ms-green-400 focus:outline-ms-green-300/75',
      [ThemeKeys.darkBlue]: 'focus:border-wl-blue-250 focus:outline-wl-blue-300/75',
      [ThemeKeys.orange]: 'focus:border-juicy-orange-500 focus:outline-juicy-orange-300/75',
      [ThemeKeys.default]: 'focus:border-dusty-black-400 focus:outline-dusty-black-300/75',
    }
    return themes[themeKey] ?? themes.default
  }

  /**
    Your text needs to be given to the @value component arg so that it can be set as the textarea's value, and as its
    yielded content. This is because a textarea needs to be provided both in order for it to display the text and also to
    update when the text value is updated by the parent.

    Note that there seems to be conflicting info on textarea value attribute.
    This page says 'value' attr not supported on textarea https://developer.mozilla.org/en-US/docs/Web/HTML/Element/textarea
    This says it is https://developer.mozilla.org/en-US/docs/Web/API/HTMLTextAreaElement
    Ember's built-in Textarea component uses the elements' value attribute too.
    https://github.com/emberjs/ember.js/blob/d334840e124a355635ba1bd5cd1dbb307daa2d7f/packages/%40ember/-internals/glimmer/lib/templates/textarea.hbs#L1-L8


  */
  <template>
    <textarea
      class="border-dusty-black-100 rounded-md border p-2 outline outline-2 outline-transparent transition duration-200
        {{this.themeClass}}"
      ...attributes
      value={{@value}}
    >{{@value}}</textarea>
  </template>
}

export default TextArea
